<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import ExportReportModal from 'dashboard/components-next/Campaigns/ExportReportModal/ExportReportModal.vue';

import CampaignsAPI from 'dashboard/api/campaigns';
import { useWhatsAppCampaignStatus } from 'dashboard/composables/useWhatsAppCampaignStatus';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  campaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'close',
  'pause',
  'resume',
  'stop',
  'cancel',
  'delete',
]);

const { t } = useI18n();

// --- State ---
const report = ref(null);
const isLoading = ref(false);
const hasError = ref(false);
const activeTabIndex = ref(0);
const lastUpdatedAt = ref(null);
const statusFilter = ref('all');
const showExportModal = ref(false);

let pollingInterval = null;

const POLL_INTERVAL_MS = 10000;

// --- Computed: Status helpers ---
const executionStatus = computed(() => report.value?.execution?.status || '');

const {
  isPaused,
  isStopped,
  isCompleted,
  statusLabel,
  statusBadgeClass,
  canPause,
  canResume,
  canStop,
  canCancel,
  shouldPoll,
} = useWhatsAppCampaignStatus(
  () => props.campaign?.campaign_status,
  executionStatus
);

// --- Computed: KPI data ---
const deliveryStats = computed(() => report.value?.delivery_stats || {});
const contactDetails = computed(() => report.value?.contact_details || []);

const kpiItems = computed(() => {
  const s = deliveryStats.value;
  return [
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.TOTAL_CONTACTS'),
      value: s.total_contacts ?? 0,
      type: 'count',
    },
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.SUBMITTED'),
      value: s.submitted ?? 0,
      type: 'count',
    },
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.SENT'),
      value: s.sent ?? 0,
      type: 'count',
    },
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.DELIVERED'),
      value: s.delivered ?? 0,
      type: 'count',
    },
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.READ'),
      value: s.read ?? 0,
      type: 'count',
    },
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.FAILED'),
      value: s.failed ?? 0,
      type: 'count',
      color: 'ruby',
    },
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.SKIPPED'),
      value: s.skipped ?? 0,
      type: 'count',
    },
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.PENDING'),
      value: s.pending ?? 0,
      type: 'count',
    },
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.DELIVERY_RATE'),
      value: s.delivery_rate ?? 0,
      type: 'rate',
      color: 'teal',
    },
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.READ_RATE'),
      value: s.read_rate ?? 0,
      type: 'rate',
      color: 'blue',
    },
    {
      label: t('CAMPAIGN.WHATSAPP.DETAIL.KPI.FAILURE_RATE'),
      value: s.failure_rate ?? 0,
      type: 'rate',
      color: 'ruby',
    },
  ];
});

// --- Computed: Chart data ---
const chartCollection = computed(() => {
  const s = deliveryStats.value;
  const labels = [
    t('CAMPAIGN.WHATSAPP.DETAIL.KPI.PENDING'),
    t('CAMPAIGN.WHATSAPP.DETAIL.KPI.SUBMITTED'),
    t('CAMPAIGN.WHATSAPP.DETAIL.KPI.SENT'),
    t('CAMPAIGN.WHATSAPP.DETAIL.KPI.DELIVERED'),
    t('CAMPAIGN.WHATSAPP.DETAIL.KPI.READ'),
    t('CAMPAIGN.WHATSAPP.DETAIL.KPI.FAILED'),
    t('CAMPAIGN.WHATSAPP.DETAIL.KPI.SKIPPED'),
  ];
  const data = [
    s.pending ?? 0,
    s.submitted ?? 0,
    s.sent ?? 0,
    s.delivered ?? 0,
    s.read ?? 0,
    s.failed ?? 0,
    s.skipped ?? 0,
  ];
  const backgroundColors = [
    '#94a3b8', // pending – slate
    '#60a5fa', // submitted – blue
    '#38bdf8', // sent – sky
    '#34d399', // delivered – green
    '#818cf8', // read – indigo
    '#f87171', // failed – red
    '#fbbf24', // skipped – amber
  ];
  return {
    labels,
    datasets: [
      {
        label: t('CAMPAIGN.WHATSAPP.DETAIL.CHART.TITLE'),
        data,
        backgroundColor: backgroundColors,
        borderRadius: 4,
      },
    ],
  };
});

// --- Computed: Tabs ---
const tabs = computed(() => [
  { label: t('CAMPAIGN.WHATSAPP.DETAIL.TABS.OVERVIEW') },
  {
    label: t('CAMPAIGN.WHATSAPP.DETAIL.TABS.LOGS'),
    count: contactDetails.value.length,
  },
]);

// --- Computed: Filtered contacts ---
const STATUS_DISPLAY_MAP = {
  pending: 'Pending',
  submitted: 'Submitted',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed',
  skipped: 'Skipped',
};

const filteredContacts = computed(() => {
  if (statusFilter.value === 'all') {
    return contactDetails.value;
  }
  return contactDetails.value.filter(
    c =>
      STATUS_DISPLAY_MAP[c.status]?.toLowerCase() ===
      statusFilter.value.toLowerCase()
  );
});

const canExportReport = computed(() => isCompleted.value || isStopped.value);

// --- Computed: Last updated ---
const lastUpdatedText = computed(() => {
  if (!lastUpdatedAt.value) return '';
  const timeStr = new Date(lastUpdatedAt.value).toLocaleTimeString();
  return t('CAMPAIGN.WHATSAPP.DETAIL.LAST_UPDATED', { time: timeStr });
});

// --- Methods ---
const fetchReport = async () => {
  if (!props.campaign?.id) return;

  isLoading.value = true;
  hasError.value = false;

  try {
    const { data } = await CampaignsAPI.deliveryStatus(props.campaign.id);
    report.value = data;
    lastUpdatedAt.value = Date.now();
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const startPolling = () => {
  stopPolling();
  if (shouldPoll.value) {
    pollingInterval = setInterval(fetchReport, POLL_INTERVAL_MS);
  }
};

const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
};

const handleClose = () => {
  stopPolling();
  emit('close');
};

const handleTabChanged = tab => {
  activeTabIndex.value = tabs.value.findIndex(t => t.label === tab.label);
};

const handleExportClick = () => {
  showExportModal.value = true;
};

const formatProcessedAt = timestamp => {
  if (!timestamp) return '—';
  return new Date(timestamp).toLocaleString();
};

const getStatusDisplayLabel = status => {
  return STATUS_DISPLAY_MAP[status] || status;
};

const getStatusBadgeColor = status => {
  const colorMap = {
    pending: 'bg-n-alpha-2 text-n-slate-11',
    submitted: 'bg-n-blue-3 text-n-blue-11',
    sent: 'bg-n-blue-3 text-n-blue-11',
    delivered: 'bg-n-teal-3 text-n-teal-11',
    read: 'bg-n-violet-3 text-n-violet-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    skipped: 'bg-n-amber-3 text-n-amber-11',
  };
  return colorMap[status] || 'bg-n-alpha-2 text-n-slate-11';
};

const refresh = () => {
  fetchReport();
};

defineExpose({ refresh });

// --- Watchers ---
watch(
  () => props.show,
  newVal => {
    if (newVal && props.campaign) {
      activeTabIndex.value = 0;
      statusFilter.value = 'all';
      report.value = null;
      fetchReport().then(() => startPolling());
    } else {
      stopPolling();
    }
  }
);

watch(
  () => shouldPoll.value,
  () => {
    if (props.show) {
      if (shouldPoll.value) {
        startPolling();
      } else {
        stopPolling();
      }
    }
  }
);

onBeforeUnmount(() => {
  stopPolling();
});
</script>

<template>
  <TeleportWithDirection to="body">
    <transition name="drawer">
      <div v-if="show" class="fixed inset-0 z-50 flex justify-end">
        <!-- Overlay -->
        <div
          class="absolute inset-0 bg-black/40 transition-opacity"
          @click="handleClose"
        />

        <!-- Drawer panel -->
        <div
          class="relative flex flex-col w-[44rem] max-w-full h-full bg-n-solid-1 shadow-xl"
        >
          <!-- Header -->
          <div
            class="flex flex-col gap-1 px-6 pt-5 pb-4 border-b border-n-strong"
          >
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 min-w-0">
                <h2 class="text-lg font-semibold text-n-slate-12 truncate">
                  {{ campaign?.title }}
                </h2>
                <span
                  class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md shrink-0"
                  :class="statusBadgeClass"
                >
                  {{ statusLabel }}
                </span>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <Button
                  v-if="canPause"
                  variant="faded"
                  size="sm"
                  color="amber"
                  icon="i-lucide-pause"
                  :label="t('CAMPAIGN.LIST.BUTTONS.PAUSE')"
                  @click="emit('pause', campaign)"
                />
                <Button
                  v-if="canResume"
                  variant="faded"
                  size="sm"
                  color="teal"
                  icon="i-lucide-play"
                  :label="t('CAMPAIGN.LIST.BUTTONS.RESUME')"
                  @click="emit('resume', campaign)"
                />
                <Button
                  v-if="canCancel"
                  variant="faded"
                  size="sm"
                  color="ruby"
                  icon="i-lucide-x-circle"
                  :label="t('CAMPAIGN.LIST.BUTTONS.CANCEL')"
                  @click="emit('cancel', campaign)"
                />
                <Button
                  v-if="canStop"
                  variant="faded"
                  size="sm"
                  color="ruby"
                  icon="i-lucide-square"
                  :label="t('CAMPAIGN.LIST.BUTTONS.STOP')"
                  @click="emit('stop', campaign)"
                />
                <Button
                  v-if="canExportReport"
                  variant="faded"
                  size="sm"
                  color="blue"
                  icon="i-lucide-download"
                  :label="t('CAMPAIGN.LIST.BUTTONS.EXPORT_REPORT')"
                  @click="handleExportClick"
                />
                <Button
                  variant="faded"
                  size="sm"
                  color="ruby"
                  icon="i-lucide-trash"
                  @click="emit('delete', campaign)"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  color="slate"
                  icon="i-lucide-x"
                  @click="handleClose"
                />
              </div>
            </div>
            <span v-if="lastUpdatedText" class="text-xs text-n-slate-10">
              {{ lastUpdatedText }}
            </span>
          </div>

          <!-- Content area -->
          <div class="flex flex-col flex-1 min-h-0">
            <!-- Loading state -->
            <div
              v-if="isLoading && !report"
              class="flex items-center justify-center flex-1 text-n-slate-11"
            >
              <Spinner />
            </div>

            <!-- Error state -->
            <div
              v-else-if="hasError && !report"
              class="flex flex-col items-center justify-center flex-1 gap-3"
            >
              <span class="i-lucide-alert-triangle text-2xl text-n-ruby-11" />
              <p class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.WHATSAPP.DETAIL.ERROR_STATE') }}
              </p>
              <Button
                variant="faded"
                size="sm"
                color="blue"
                :label="t('CAMPAIGN.WHATSAPP.DETAIL.RETRY')"
                @click="fetchReport"
              />
            </div>

            <!-- Tabbed content -->
            <template v-else-if="report">
              <div class="px-6 pt-4">
                <TabBar
                  :tabs="tabs"
                  :initial-active-tab="activeTabIndex"
                  @tab-changed="handleTabChanged"
                />
              </div>

              <!-- Overview tab -->
              <div
                v-if="activeTabIndex === 0"
                class="flex-1 px-6 py-4 overflow-y-auto"
              >
                <!-- KPI Grid -->
                <div class="grid grid-cols-4 gap-3 mb-6">
                  <div
                    v-for="kpi in kpiItems"
                    :key="kpi.label"
                    class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
                  >
                    <span class="text-xs text-n-slate-10 truncate">
                      {{ kpi.label }}
                    </span>
                    <span
                      class="text-lg font-semibold"
                      :class="
                        kpi.color === 'ruby'
                          ? 'text-n-ruby-11'
                          : kpi.color === 'teal'
                            ? 'text-n-teal-11'
                            : kpi.color === 'blue'
                              ? 'text-n-blue-11'
                              : 'text-n-slate-12'
                      "
                    >
                      {{
                        kpi.type === 'rate'
                          ? `${Number(kpi.value).toFixed(1)}%`
                          : kpi.value
                      }}
                    </span>
                  </div>
                </div>

                <!-- Bar chart -->
                <div class="p-4 rounded-lg bg-n-alpha-1 border border-n-weak">
                  <h3 class="text-sm font-medium text-n-slate-12 mb-3">
                    {{ t('CAMPAIGN.WHATSAPP.DETAIL.CHART.TITLE') }}
                  </h3>
                  <div class="h-56">
                    <BarChart :collection="chartCollection" />
                  </div>
                </div>
              </div>

              <!-- Campaign logs tab -->
              <div
                v-if="activeTabIndex === 1"
                class="flex flex-col flex-1 min-h-0 px-6 py-4"
              >
                <!-- Status filter -->
                <div class="flex items-center gap-2 mb-3">
                  <select
                    v-model="statusFilter"
                    class="text-sm px-3 py-1.5 rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-7"
                  >
                    <option value="all">
                      {{ t('CAMPAIGN.WHATSAPP.DETAIL.LOGS.FILTER_ALL') }}
                    </option>
                    <option
                      v-for="(label, key) in STATUS_DISPLAY_MAP"
                      :key="key"
                      :value="label"
                    >
                      {{ label }}
                    </option>
                  </select>
                </div>

                <!-- Table -->
                <div
                  class="flex-1 overflow-auto rounded-lg border border-n-weak"
                >
                  <table class="w-full text-sm">
                    <thead
                      class="sticky top-0 bg-n-alpha-1 border-b border-n-weak"
                    >
                      <tr>
                        <th
                          class="px-3 py-2 text-left text-xs font-medium text-n-slate-10"
                        >
                          {{ t('CAMPAIGN.WHATSAPP.DETAIL.LOGS.COL_CONTACT') }}
                        </th>
                        <th
                          class="px-3 py-2 text-left text-xs font-medium text-n-slate-10"
                        >
                          {{ t('CAMPAIGN.WHATSAPP.DETAIL.LOGS.COL_PHONE') }}
                        </th>
                        <th
                          class="px-3 py-2 text-left text-xs font-medium text-n-slate-10"
                        >
                          {{ t('CAMPAIGN.WHATSAPP.DETAIL.LOGS.COL_STATUS') }}
                        </th>
                        <th
                          class="px-3 py-2 text-left text-xs font-medium text-n-slate-10"
                        >
                          {{
                            t('CAMPAIGN.WHATSAPP.DETAIL.LOGS.COL_PROCESSED_AT')
                          }}
                        </th>
                        <th
                          class="px-3 py-2 text-left text-xs font-medium text-n-slate-10"
                        >
                          {{ t('CAMPAIGN.WHATSAPP.DETAIL.LOGS.COL_ERROR') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-n-weak">
                      <tr
                        v-for="(contact, idx) in filteredContacts"
                        :key="idx"
                        class="hover:bg-n-alpha-1 transition-colors"
                      >
                        <td class="px-3 py-2 text-n-slate-12 truncate max-w-40">
                          {{ contact.contact_name || '—' }}
                        </td>
                        <td class="px-3 py-2 text-n-slate-11 font-mono text-xs">
                          {{ contact.contact_phone || '—' }}
                        </td>
                        <td class="px-3 py-2">
                          <span
                            class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                            :class="getStatusBadgeColor(contact.status)"
                          >
                            {{ getStatusDisplayLabel(contact.status) }}
                          </span>
                        </td>
                        <td class="px-3 py-2 text-n-slate-11 text-xs">
                          {{ formatProcessedAt(contact.processed_at) }}
                        </td>
                        <td
                          class="px-3 py-2 text-n-ruby-11 text-xs max-w-40 truncate"
                        >
                          {{
                            contact.status === 'failed'
                              ? contact.error_message || '—'
                              : ''
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <!-- Empty logs -->
                  <div
                    v-if="filteredContacts.length === 0"
                    class="flex items-center justify-center py-10 text-sm text-n-slate-10"
                  >
                    {{ t('CAMPAIGN.WHATSAPP.DETAIL.LOGS.NO_LOGS') }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </TeleportWithDirection>

  <ExportReportModal
    :show="showExportModal"
    :campaign="{
      id: campaign?.id,
      title: campaign?.title,
      campaign_status: campaign?.campaign_status,
      inbox: campaign?.inbox,
    }"
    @close="showExportModal = false"
  />
</template>
